import Button from '@risksmart-app/components/src/button';
import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import SimpleRatingBadge from '@risksmart-app/components/src/simple-rating-badge/SimpleRatingBadge';
import { IndicatorType as Indicator_Type_Enum } from '@risksmart-app/domain/src/types/consts/indicator-type';
import type { IndicatorBandingConfig } from '@risksmart-app/domain/src/types/indicator-banding-config';
import _ from 'lodash';
import type { MutableRefObject } from 'react';
import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import type { useGetIndicatorById } from 'src/hooks/queries/indicator/useGetIndicatorById';
import type { useGetIndicatorResultsByIndicatorId } from 'src/hooks/queries/indicator/useGetIndicatorResultsByIndicatorId';
import type { IndicatorThresholds } from 'src/pages/indicators/calculateConformanceRating';
import {
  calculatePercentageDifference,
  conformanceIndicatorRating,
  getConformanceTrendRating,
  getFrozenConformanceTrend,
  percentageDifferenceValue,
} from 'src/pages/indicators/calculateConformanceRating';
import type {
  FrozenBandDisplay,
  FrozenBandStamp,
} from 'src/pages/indicators/resolveFrozenBandDisplay';
import { resolveFrozenBandDisplay } from 'src/pages/indicators/resolveFrozenBandDisplay';
import type { ConformanceTrend } from 'src/pages/indicators/types';
import { ConformanceIndicatorRating } from 'src/pages/indicators/types';
import { Permission } from 'src/rbac/Permission';
import type { UseGetTablePropsOptions } from 'src/utils/table/hooks/useGetStatelessTableProps';
import { useGetTableProps } from 'src/utils/table/hooks/useGetTableProps';
import type { TableFields, TablePropsWithActions } from 'src/utils/table/types';

import Link from '@/components/link';
import PolarityBadge from '@/components/polarity-badge/PolarityBadge';
import type { IndicatorBandingConfigById } from '@/hooks/queries/indicator/useGetIndicatorBandingConfigsByIds';
import { useGetIndicatorBandingConfigsByIds } from '@/hooks/queries/indicator/useGetIndicatorBandingConfigsByIds';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { toLocalDate } from '@/utils/dateUtils';
import { compareNullableNumber } from '@/utils/numberUtils';
import { useSortingDirectionTracker } from '@/utils/table/hooks/useSortingDirectionTracker';

type GetIndicatorResultsByIndicatorIdData = NonNullable<
  ReturnType<typeof useGetIndicatorResultsByIndicatorId>['data']
>;

type GetIndicatorByIdRow = NonNullable<
  ReturnType<typeof useGetIndicatorById>['data']
>['indicator'][number];

export type IndicatorResultFields =
  GetIndicatorResultsByIndicatorIdData['indicator_result'][number] & {
    previous?: GetIndicatorResultsByIndicatorIdData['indicator_result'][number];
  };

/**
 * Minimal shape the Result column's display + sort comparator need — kept
 * structural (not a `Pick` of the generated row type) so callers and tests can
 * pass lightweight objects, matching the existing `getResult` convention.
 */
type ResultSortFields = {
  TargetValueNum?: null | number;
  TargetValueTxt?: null | string;
  parent?: { Type: Indicator_Type_Enum } | null;
};

type IndicatorTableFields = IndicatorResultFields & {
  ModifiedByUserName: string;
  Conformance: ConformanceIndicatorRating;
  ConformanceTrend: ConformanceTrend | null;
  PercentageChange: string;
  Result: string;
};

const useLabelledFields = (
  indicator: GetIndicatorByIdRow,
  data: GetIndicatorResultsByIndicatorIdData | undefined,
  isFrozenBandingActive: boolean
) => {
  return useMemo<IndicatorTableFields[]>(() => {
    const labelledFields =
      data?.indicator_result.map((r, i) => ({
        ...r,
        previous: data.indicator_result[i - 1],
      })) || [];

    return labelledFields.map((item) => ({
      ...item,
      ModifiedByUserName: item.modifiedBy?.FriendlyName || '-',
      Description: item.Description || '-',
      Conformance: conformanceIndicatorRating(indicator, item),
      PercentageChange: getPercentageChange(item),
      ConformanceTrend: isFrozenBandingActive
        ? getFrozenConformanceTrendValue(item)
        : getConformanceTrendRatingValue(indicator, item),
      Result: getResult(item),
    }));
  }, [data, indicator, isFrozenBandingActive]);
};

const useGetFieldConfig = (
  onEdit: (indicator: IndicatorTableFields) => void,
  // Mutable ref kept in sync with the table's live sort direction so the
  // Result / Percentage change comparators pin sentinel rows (no value / "-")
  // to the bottom in both directions. See `useSortingDirectionTracker`.
  sortingDescendingRef: MutableRefObject<boolean>,
  isFrozenBandingActive: boolean,
  configById: IndicatorBandingConfigById
): TableFields<IndicatorTableFields> => {
  const { getByValue: statusGetByValue } = useRating(
    'indicator_conformance_status'
  );
  const { getByValue: trendGetByValue } = useRating(
    'indicator_conformance_trend'
  );
  const { t } = useTranslation(['common'], {
    keyPrefix: 'indicator_results.columns',
  });

  return useMemo<TableFields<IndicatorTableFields>>(
    () => ({
      ResultDate: {
        formId: 'indicator_result',
        fieldId: 'ResultDate',
        cell: (item) => (
          <Link variant={'secondary'} href={'#'} onFollow={() => onEdit(item)}>
            {toLocalDate(item.ResultDate)}
          </Link>
        ),
        sortingComparator: compareByResultDateTime,
        isRowHeader: true,
      },
      Description: {
        formId: 'indicator_result',
        fieldId: 'Description',
      },
      ModifiedByUserName: {
        header: t('modified_by'),
      },
      Result: {
        header: t('result'),
        sortingComparator: (a, b) =>
          compareByResult(a, b, sortingDescendingRef.current),
      },
      Conformance: {
        id: 'conformance',
        header: t('conformance'),
        cell: (item) => {
          const display = resolveConformanceCellDisplay(
            item,
            isFrozenBandingActive,
            configById
          );

          return display.kind === 'frozen' ? (
            <PolarityBadge
              label={display.display.label}
              colour={display.display.colour}
              polarity={display.display.polarity}
              severity={display.display.severity}
            />
          ) : (
            <SimpleRatingBadge rating={statusGetByValue(display.rating)} />
          );
        },
      },
      ConformanceTrend: {
        header: t('conformance_trend'),
        cell: (item) => {
          const rating = trendGetByValue(item.ConformanceTrend);

          return <SimpleRatingBadge rating={rating} />;
        },
      },
      PercentageChange: {
        header: t('percentage_change'),
        sortingComparator: (a, b) =>
          compareNullableNumber(
            getPercentageChangeValue(a),
            getPercentageChangeValue(b),
            sortingDescendingRef.current
          ),
        fieldType: 'number',
      },
    }),
    [
      statusGetByValue,
      trendGetByValue,
      onEdit,
      t,
      sortingDescendingRef,
      isFrozenBandingActive,
      configById,
    ]
  );
};

/**
 * Sort comparator for the Result column. Number-type indicators sort by the
 * numeric `TargetValueNum` (so 100 > 30 > 5, not the lexicographic "100" < "30"
 * < "5"); other indicator types fall back to a locale-aware compare of the
 * displayed value. Number rows with no value pin to the bottom in both
 * directions.
 */
export const compareByResult = (
  a: ResultSortFields,
  b: ResultSortFields,
  isDescending: boolean
): number => {
  const isNumberIndicator =
    a.parent?.Type === Indicator_Type_Enum.Number ||
    b.parent?.Type === Indicator_Type_Enum.Number;

  if (isNumberIndicator) {
    return compareNullableNumber(
      a.TargetValueNum ?? null,
      b.TargetValueNum ?? null,
      isDescending
    );
  }

  return getResult(a).localeCompare(getResult(b));
};

/**
 * Sorts by the full ResultDate datetime, falling back to CreatedAtTimestamp so
 * results sharing the same ResultDate (e.g. date-only entries stored at
 * midnight) are ordered by when they were recorded.
 */
export const compareByResultDateTime = (
  a: Pick<IndicatorResultFields, 'ResultDate' | 'CreatedAtTimestamp'>,
  b: Pick<IndicatorResultFields, 'ResultDate' | 'CreatedAtTimestamp'>
): number => {
  const resultDateDiff =
    new Date(a.ResultDate).getTime() - new Date(b.ResultDate).getTime();
  if (resultDateDiff !== 0) {
    return resultDateDiff;
  }

  return (
    new Date(a.CreatedAtTimestamp).getTime() -
    new Date(b.CreatedAtTimestamp).getTime()
  );
};

const getConformanceTrendRatingValue = (
  indicator: IndicatorThresholds,
  item: IndicatorResultFields
) => {
  const results = [item];
  if (item.previous) {
    results.push(item.previous);
  }

  return getConformanceTrendRating(indicator, results);
};

/**
 * CUS-188 `configurable_thresholds` flag-on sibling of
 * {@link getConformanceTrendRatingValue} — same [item, item.previous]
 * newest-first construction, but derives the trend from frozen `BandSeverity`
 * stamps via {@link getFrozenConformanceTrend} instead of live-recalculating
 * against the indicator's current thresholds.
 */
const getFrozenConformanceTrendValue = (item: IndicatorResultFields) => {
  const results = [item];
  if (item.previous) {
    results.push(item.previous);
  }

  return getFrozenConformanceTrend(results);
};

export type ConformanceCellDisplay =
  | { kind: 'frozen'; display: FrozenBandDisplay }
  | { kind: 'rating'; rating: ConformanceIndicatorRating };

/**
 * Decides what the results-tab Conformance cell renders. Kept structural
 * (not a `Pick` of the generated row type) so callers and tests can pass
 * lightweight objects, matching the existing `ResultSortFields` convention.
 *
 * Flag off always uses the live-evaluated `item.Conformance`. Flag on reads
 * the result row's OWN frozen `BandLabel`/`BandPolarity`/`BandSeverity`/
 * `BandingConfigId` stamp — never `item.Conformance` — so a later band edit
 * (M2) can't change how an already-logged result is displayed. An unstamped
 * or unresolvable row falls back to NotSet, not to the live evaluation.
 */
export const resolveConformanceCellDisplay = (
  item: FrozenBandStamp & { Conformance: ConformanceIndicatorRating },
  isFrozenBandingActive: boolean,
  configById: Map<string, { config: IndicatorBandingConfig }>
): ConformanceCellDisplay => {
  if (!isFrozenBandingActive) {
    return { kind: 'rating', rating: item.Conformance };
  }

  const display = resolveFrozenBandDisplay(item, configById);

  return display
    ? { kind: 'frozen', display }
    : { kind: 'rating', rating: ConformanceIndicatorRating.NotSet };
};

const getPercentageChange = (item: IndicatorResultFields) => {
  if (item.previous?.TargetValueNum == null || item.TargetValueNum == null) {
    return '-';
  }

  return calculatePercentageDifference(
    item.TargetValueNum,
    item.previous.TargetValueNum
  );
};

/**
 * Numeric value backing the Percentage change column's sort. Mirrors
 * {@link getPercentageChange} but returns the signed number (or `null` for the
 * "-" cases: no previous result, or a zero previous value) so the column sorts
 * numerically rather than lexicographically on the formatted "x.xx%" string.
 */
export const getPercentageChangeValue = (item: {
  TargetValueNum?: null | number;
  previous?: { TargetValueNum?: null | number } | null;
}): number | null => {
  if (item.previous?.TargetValueNum == null || item.TargetValueNum == null) {
    return null;
  }

  return percentageDifferenceValue(
    item.TargetValueNum,
    item.previous.TargetValueNum
  );
};

export const getResult = (item: ResultSortFields): string => {
  let result = '-';
  if (
    item.parent?.Type === Indicator_Type_Enum.Number &&
    !_.isNil(item.TargetValueNum)
  ) {
    result = `${item.TargetValueNum}`;
  }
  if (item.parent?.Type === Indicator_Type_Enum.Text && item.TargetValueTxt) {
    result = item.TargetValueTxt;
  }

  return result;
};

const useGetIndicatorTableProps = (
  indicator: GetIndicatorByIdRow,
  data: GetIndicatorResultsByIndicatorIdData | undefined,
  onEdit: (indicator: IndicatorTableFields) => void,
  handleModalOpen: () => void,
  sortingDescendingRef: MutableRefObject<boolean>
): UseGetTablePropsOptions<IndicatorTableFields> => {
  const isConfigurableThresholdsEnabled = useIsFeatureFlagEnabled(
    'configurable_thresholds'
  );
  // Numeric only: text/boolean results are never band-stamped (CUS-185 seeds
  // and stamps `number` indicators alone), so the frozen path would render
  // every row "Not set" and trend it as indeterminate, losing the legacy
  // target-match behaviour the flag-off build still shows. This tab is scoped
  // to one indicator, so the check folds in here rather than per row.
  const isFrozenBandingActive =
    isConfigurableThresholdsEnabled &&
    indicator.Type === Indicator_Type_Enum.Number;
  // Every row's own pinned config must be resolved — unlike the register
  // (which only needs the latest result), this table shows a full history
  // that may span several config versions edited over time (M2).
  const distinctConfigIds = useMemo(
    () => [
      ...new Set(
        (data?.indicator_result ?? []).flatMap((r) =>
          r.BandingConfigId ? [r.BandingConfigId] : []
        )
      ),
    ],
    [data]
  );
  const { configById } = useGetIndicatorBandingConfigsByIds(distinctConfigIds, {
    isSkipped: !isFrozenBandingActive,
  });
  const labelledFields = useLabelledFields(
    indicator,
    data,
    isFrozenBandingActive
  );
  const { t } = useTranslation(['common'], { keyPrefix: 'indicator_results' });
  const fields = useGetFieldConfig(
    onEdit,
    sortingDescendingRef,
    isFrozenBandingActive,
    configById
  );

  return {
    tableId: 'indicatorTab',
    data: labelledFields,
    customAttributeFormIds: ['indicator_result'],
    entityLabel: t('entity_name'),
    emptyCollectionAction: (
      <Permission
        permission={'insert:indicator_result'}
        parentObject={indicator}
      >
        <Button formAction={'none'} onClick={handleModalOpen}>
          {t('create_new_button')}
        </Button>
      </Permission>
    ),
    preferencesStorageKey: 'IndicatorTabTable-Preferences',
    enableFiltering: false,
    initialColumns: [
      'ResultDate',
      'Description',
      'ModifiedByUserName',
      'Result',
      'Conformance',
      'ConformanceTrend',
      'PercentageChange',
    ],
    fields,
    defaultSortingState: {
      sortingColumn: 'ResultDate',
      sortingDirection: 'desc',
    },
  };
};

export const useGetCollectionTableProps = (
  indicator: GetIndicatorByIdRow,
  data: GetIndicatorResultsByIndicatorIdData | undefined,
  onEdit: (indicator: IndicatorTableFields) => void,
  handleModalOpen: () => void
): TablePropsWithActions<IndicatorTableFields> => {
  const sortingDescendingRef = useRef(false);
  const props = useGetIndicatorTableProps(
    indicator,
    data,
    onEdit,
    handleModalOpen,
    sortingDescendingRef
  );
  const tableProps = useGetTableProps(props);

  return useSortingDirectionTracker(tableProps, sortingDescendingRef);
};
